import React, { useState } from 'react';
import { toast } from '../../../../ui/toast';
import {
  BriefcaseBusinessIcon,
  ChartIcons,
  ClipBoardIcons,
  DashboardIcons,
  LightningIcons,
  NotebookIcons,
} from '../../../../icons/icons';
import { NameEmailAvatar } from '../../../../ui';
import {
  createApplicantNote,
  deleteApplicantNote,
} from '../../../../utils/api_service/JobOpenings';
import { Applicant } from '../../../../utils/types/JobOpenings';
import {
  AddNoteDialog,
  DocumentsTab,
  EducationTab,
  ExperienceTab,
  NotesTab,
  OverviewTab,
  PreQualResultsTab,
  SkillsTab,
} from './Components';
import { useCandidate } from './Hooks/UseCandidate';
import { PDFDisplay } from './PDFDisplay';
import { CCAResultsTab } from './Tabs/CCAResultsTab';

// Enhanced animation styles with better performance
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  .animate-slideDown {
    animation: slideDown 0.2s ease-out;
  }
  
  /* Custom scrollbar styles */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #10b981;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #059669;
  }
`;

export interface ICandidateProfile {
  selectedCandidate: Applicant;
  handleSendEmailToSingleApplicant?: (
    applicant: Applicant,
    updateStatusFirst?: boolean,
  ) => Promise<void>;
}

export const CandidateProfile = (props: ICandidateProfile) => {
  const { selectedCandidate, handleSendEmailToSingleApplicant } = props;
  const {
    isOpen,
    applicant,
    loading,
    optionClick,
    isHighlightHit,
    leftWidth,
    activeTab,
    containerRef,
    dropdownRef,
    handleButtonClick,
    handleOptionClick,
    options,
    setActiveTab,
    getJobApplicants,
    selectedDocument,
    setSelectedDocument,
    pdfUrl,
    docLoading,
    handleResendInvite,
    isResending,
    id,
  } = useCandidate({ selectedCandidate, handleSendEmailToSingleApplicant });

  // State for Add Note Dialog
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Compute matched skills and behavioral attributes based on isHighlightHit
  // skills are the matched skills taken from the JD
  const matchedSkills = selectedCandidate?.matchedSkills || [];
  const fullName =
    selectedCandidate?.name ||
    `${selectedCandidate?.firstName || ''} ${
      selectedCandidate?.lastName || ''
    }`.trim() ||
    'Candidate';

  // Handle saving note
  const handleSaveNote = async (note: any) => {
    try {
      const response = await createApplicantNote(
        selectedCandidate.userId,
        note,
      );
      if (response.ok) {
        getJobApplicants();
      }
    } catch (e) {
      console.log(e);
    }
    // TODO: Implement API call to save note
    setIsNoteDialogOpen(false);
    // You can add API call here to save the note to backend
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteApplicantNote(id);
      if (response.ok) {
        getJobApplicants();
        setIsModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    }
    // call API, remove from state
  };

  const handleDownloadCV = async () => {
    if (!applicant?.pdfUrl) return;

    let url = applicant.pdfUrl;
    if (!url.startsWith('http')) {
      url = `${window.location.origin}${url}`;
    }

    const toastId = toast.info('Preparing download...', { autoClose: false });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const extension = url.split('?')[0].split('.').pop() || 'pdf';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Download failed:', error);
      toast.dismiss(toastId);

      // Fallback to standard download if fetch fails (e.g. CORS)
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      const extension = url.split('?')[0].split('.').pop() || 'pdf';
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.warn(
        'Download initiated via fallback. Checking extension compatibility.',
      );
    }
  };

  // Tab configuration for better maintainability
  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: <DashboardIcons /> },
    { id: 'skills' as const, label: 'Skills', icon: <LightningIcons /> },
    {
      id: 'experience' as const,
      label: 'Experience',
      icon: <BriefcaseBusinessIcon />,
    },
    { id: 'education' as const, label: 'Education', icon: <NotebookIcons /> },
    {
      id: 'prequal' as const,
      label: 'Pre-Qual',
      icon: <ClipBoardIcons />,
    },
    { id: 'notes' as const, label: 'Notes', icon: <NotebookIcons /> },
    { id: 'documents' as const, label: 'Documents', icon: <ClipBoardIcons /> },
    { id: 'cca' as const, label: 'CCA Results', icon: <ChartIcons /> },
  ];

  return (
    <>
      <style>{ styles }</style>
      <div
          ref={containerRef}
          className='flex flex-row w-full h-screen overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100'
      >
        { /* Left panel */ }
        <div className='flex flex-col w-full bg-white border-r border-gray-200 h-full shadow-sm'>
          { /* Header Section - Fixed */ }
          <div className=' border-b border-gray-200 bg-gradient-to-r from-white to-gray-50'>
            <div className='flex flex-col gap-4 px-2'>
              { /* Avatar and Move Button Row */ }
              <div className='flex w-full items-start justify-between'>
                <div className='flex-shrink-0'>
                  <NameEmailAvatar
                      avatar={
                        selectedCandidate.symbol &&
                      selectedCandidate.symbol !== 'image://null' &&
                      selectedCandidate.symbol !== 'image://undefined'
                          ? selectedCandidate.symbol.replace('image://', '')
                          : ''
                      }
                      email={applicant?.email ?? 'N/A'}
                      name={selectedCandidate.name}
                      applicantStatus={
                      // Normalize status display - show both Unsuccessful-Closed and Unsuccessful-Pending as 'Unsuccessful'
                        applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                      applicant?.applicantStatus === 'Unsuccessful-Pending'
                          ? 'Unsuccessful'
                          : applicant?.applicantStatus ??
                          applicant?.status ??
                          'N/A'
                      }
                  />
                </div>

                { /* Action Buttons */ }
                <div className='flex gap-2'>
                  { applicant?.pdfUrl && (
                    <button
                        onClick={handleDownloadCV}
                        className='group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                        title='Download CV/Resume'
                        aria-label='Download candidate CV or resume'
                    >
                      <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <span>Download CV</span>
                    </button>
                  ) }

                  { /* Add Note Button */ }
                  <button
                      onClick={() => setIsNoteDialogOpen(true)}
                      className='group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                      aria-label='Add note about candidate'
                  >
                    <svg
                        className='w-4 h-4'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                    <span>Add Note</span>
                  </button>

                  { /* Move To Dropdown */ }

                  <div className='relative' ref={dropdownRef}>
                    <button
                        onClick={handleButtonClick}
                        className='group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                        aria-label='Move candidate to different status'
                        aria-expanded={isOpen}
                    >
                      <span>Move to</span>
                      <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                        />
                      </svg>
                    </button>

                    { isOpen && (
                      <div className='absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl w-52 z-30 overflow-hidden animate-slideDown'>
                        { options.map((option, idx) => (
                          <button
                              key={idx}
                              onClick={() => handleOptionClick(option)}
                              className='w-full text-left px-4 py-2.5 text-sm cursor-pointer text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-150 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-green-50'
                          >
                            { option }
                          </button>
                        )) }
                      </div>
                    ) }
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='flex-shrink-0 border-b border-gray-200 bg-white'>
            <nav className='flex space-x-8 px-6' aria-label='Tabs'>
              { tabs.map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-[10px] transition-all duration-200 flex items-center gap-1 ${
                      activeTab === tab.id
                        ? 'border-green-600 text-green-600 bg-green-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <span className='scale-50 origin-center'>{ tab.icon }</span>
                  { tab.label }
                </button>
              )) }
            </nav>
          </div>
          { /* Scrollable Content Area */ }
          <div className='flex-1 flex overflow-hidden min-h-0'>
            <div
                className='flex-1 flex flex-col min-w-0'
                style={{ width: `${leftWidth}%` }}
            >
              <div className='flex-1 overflow-y-auto custom-scrollbar'>
                { /* Overview Tab */ }
                { activeTab === 'overview' && (
                  <div
                      role='tabpanel'
                      id='overview-panel'
                      aria-labelledby='overview-tab'
                  >
                    <OverviewTab
                        address={applicant?.contactInfo?.address}
                        applicantStatus={
                        // Normalize status display - show both Unsuccessful-Closed and Unsuccessful-Pending as 'Unsuccessful'
                          applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                        applicant?.applicantStatus === 'Unsuccessful-Pending'
                            ? 'Unsuccessful'
                            : applicant?.applicantStatus ??
                            applicant?.status ??
                            'N/A'
                        }
                        email={applicant?.email ?? 'N/A'}
                        phone={applicant?.contactInfo?.phone ?? 'N/A'}
                        socials={applicant?.socials}
                        notes={(applicant as any)?.notes}
                        aiScore={(applicant as any)?.aiScore}
                        statusHistory={applicant?.statusHistory || (selectedCandidate as any)?.statusHistory}
                        createdAt={applicant?.createdAt || (selectedCandidate as any)?.createdAt}
                    />
                  </div>
                ) }

                { /* Skills Tab */ }
                { activeTab === 'skills' && (
                  <div
                      role='tabpanel'
                      id='skills-panel'
                      aria-labelledby='skills-tab'
                  >
                    <SkillsTab
                        skills={applicant?.skills}
                        isHighlightHit={isHighlightHit}
                        matchedSkills={matchedSkills}
                        behavioralAttributes={applicant?.behavioralAttributes}
                        matchedBehavior={selectedCandidate.matchedBehavior}
                    />
                  </div>
                ) }

                { /* Experience Tab */ }
                { activeTab === 'experience' && (
                  <div
                      role='tabpanel'
                      id='experience-panel'
                      aria-labelledby='experience-tab'
                  >
                    <ExperienceTab experience={applicant?.workExperience} />
                  </div>
                ) }

                { /* Education Tab */ }
                { activeTab === 'education' && (
                  <div
                      role='tabpanel'
                      id='education-panel'
                      aria-labelledby='education-tab'
                  >
                    <EducationTab educations={applicant?.educations} />
                  </div>
                ) }

                { /* Pre-Qualifying Results Tab */ }
                { activeTab === 'prequal' && (
                  <div
                      role='tabpanel'
                      id='prequal-panel'
                      aria-labelledby='prequal-tab'
                  >
                    <PreQualResultsTab
                        jobOpeningId={id!}
                        jobApplicantId={selectedCandidate.userId}
                        handleResendInvite={handleResendInvite}
                        isResending={isResending}
                        preQualSentAt={applicant?.preQualSentAt}
                    />
                  </div>
                ) }

                { /* Notes Tab */ }
                { activeTab === 'notes' && (
                  <div
                      role='tabpanel'
                      id='notes-panel'
                      aria-labelledby='notes-tab'
                  >
                    <NotesTab
                        notes={(applicant as any)?.conversationNotes}
                        getJobApplicants={getJobApplicants}
                        onDelete={handleDelete}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                    />
                  </div>
                ) }
                { activeTab === 'documents' && (
                  <div
                      role='tabpanel'
                      id='documents-panel'
                      aria-labelledby='documents-tab'
                  >
                    <DocumentsTab
                        documents={applicant?.applicantDocuments || []}
                        selectedDocumentId={selectedDocument?.id}
                        onDocumentSelect={setSelectedDocument}
                        pdfUrl={applicant?.pdfUrl}
                        baseFilename={applicant?.baseFilename}
                        uploadedAt={applicant?.createdAt}
                    />
                  </div>
                ) }
                { activeTab === 'cca' && (
                  <div role='tabpanel' id='cca-panel' aria-labelledby='cca-tab'>
                    <CCAResultsTab candidate={selectedCandidate} />
                  </div>
                ) }
              </div>
            </div>
            { /* Resizer */ }
            <div
                id='resizer'
                className='w-1 cursor-col-resize bg-gradient-to-b from-green-500 to-emerald-500 hover:w-2 hover:shadow-lg transition-all duration-200 h-full flex-shrink-0 relative group'
                style={{ zIndex: 20 }}
                role='separator'
                aria-label='Resize panel separator'
                aria-orientation='vertical'
            >
              <div className='absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-white opacity-0 group-hover:opacity-50 transition-opacity duration-200' />
            </div>

            { /* Right panel: PDF viewer */ }
            <div
                className='bg-gradient-to-br from-gray-100 to-gray-200 h-full flex flex-col custom-scrollbar'
                style={{ width: `${100 - leftWidth}%` }}
            >
              { loading ? (
                <div className='flex items-center justify-center h-full'>
                  <div className='text-center'>
                    <div className='relative'>
                      <div className='animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-green-600 mx-auto mb-4'></div>
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <div className='w-8 h-8 bg-green-600 rounded-full opacity-20 animate-pulse'></div>
                      </div>
                    </div>
                    <p className='text-lg text-gray-700 font-semibold'>
                      Loading Resume...
                    </p>
                    <p className='text-sm text-gray-500 mt-2'>
                      Please wait while we fetch the document
                    </p>
                  </div>
                </div>
              ) : pdfUrl || docLoading ? (
                <div className='h-full'>
                  <PDFDisplay pdfUrl={pdfUrl} />
                </div>
              ) : (
                <div className='flex items-center justify-center h-full'>
                  <div className='text-center p-8'>
                    <svg
                        className='w-20 h-20 text-gray-400 mx-auto mb-4'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                    <p className='text-lg text-gray-600 font-medium'>
                      No Resume Available
                    </p>
                    <p className='text-sm text-gray-500 mt-2'>
                      Resume not uploaded for this candidate
                    </p>
                  </div>
                </div>
              ) }
            </div>
          </div>
        </div>
      </div>

      { /* Add Note Dialog */ }
      <AddNoteDialog
          isOpen={isNoteDialogOpen}
          onClose={() => setIsNoteDialogOpen(false)}
          onSave={handleSaveNote}
          candidateName={selectedCandidate.name}
      />
    </>
  );
};
